import { Badge, Menu } from '@mantine/core';
import { variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import { usePlacedArticleMetrics } from 'app/modules/wallforming/components/content-container/hooks/usePlacedArticleMetrics';
import { ArticleThumbnail } from 'app/modules/wallforming/components/setting-container/automated-placement/ArticleThumbnail';
import { ArticleLifeCycleIcon } from 'shared/icons/ArticleLifeCycleIcon';

import type { PlacementSolution } from 'api/models';

interface PlacementSolutionDetailsContentProps {
    index: number;
    solution: PlacementSolution;
    isApplied: boolean;
}

export const PlacementSolutionDetailsContent = ({ index, solution, isApplied }: PlacementSolutionDetailsContentProps) => {
    const { t } = useTranslation();
    const totalHeight =
        solution.totalHeight ?? solution.placementInfos.reduce((sum, info) => sum + (info.totalHeight ?? 0), 0);
    const articleNumbers = solution.placementInfos.map((usage) => usage.articleNumber);
    const { metricsByArticleNumber } = usePlacedArticleMetrics(articleNumbers);

    return (
        <div className="m-0! p-0!">
            <div className="flex items-center justify-between px-4 py-1">
                <div className="text-sm font-semibold text-text-accent">
                    {index + 1}. {t('labelSolution')}
                </div>

                {isApplied && (
                    <Badge size="sm" variant={variants.badge.light}>
                        {t('labelApplied')}
                    </Badge>
                )}
            </div>

            <Menu.Divider className="m-0" />

            <div className="max-h-92 overflow-y-auto px-4 py-3 text-sm mb-2">
                <div className="grid grid-cols-[1.6fr_1fr] gap-2 rounded-md bg-background-gray-ultra-light px-3 py-2 text-sm text-text-light">
                    <div>
                        <div className="font-medium text-text-light uppercase text-xs underline">
                            {t('labelArticleCount')}
                        </div>
                        <div className="text-text-default font-medium">{solution.instanceCount}</div>
                    </div>
                    <div>
                        <div className="font-medium text-text-light uppercase text-xs underline">
                            {t('labelTotalHeight')}
                        </div>
                        <div className="text-text-default font-medium">{totalHeight.toFixed(2)}m</div>
                    </div>
                    <div>
                        <div className="font-medium text-text-light uppercase text-xs underline">
                            {t('labelVerticalOverbuild')}
                        </div>
                        <div className="text-text-default font-medium">{solution.verticalOverbuild.toFixed(2)}m</div>
                    </div>
                </div>

                <div className="mt-4 flex flex-col gap-2">
                    {solution.placementInfos.map((usage) => (
                        <div
                            key={usage.articleNumber}
                            className="rounded-md border border-border-ultra-light bg-background-default px-2 py-2"
                        >
                            <div className="flex items-center justify-between gap-2 px-1">
                                <div className="flex min-w-0 items-center gap-2 font-mono text-xs text-text-default">
                                    <span className="min-w-0 shrink truncate font-semibold">{usage.articleNumber}</span>
                                    <ArticleLifeCycleIcon
                                        lifeCycleCode={
                                            usage.lifeCycleCode ??
                                            metricsByArticleNumber.get(usage.articleNumber)?.ppmCode ??
                                            undefined
                                        }
                                    />
                                </div>
                                <Badge size="sm" variant={variants.badge.neutral}>
                                    {usage.count}
                                </Badge>
                            </div>

                            <Menu.Divider className="my-1.5 border-e-border-ultra-light" />

                            <div className="mt-2 ml-2 px-1 grid grid-cols-[1.3fr_1fr] gap-2 text-sm text-text-light">
                                <div>
                                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm border border-border-ultra-light">
                                        <ArticleThumbnail
                                            articleNumber={usage.articleNumber}
                                            thumbnailUri={
                                                metricsByArticleNumber.get(usage.articleNumber)?.thumbnailUri ??
                                                undefined
                                            }
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="font-medium text-text-light uppercase text-xs underline">
                                        {t('labelHeight')}
                                    </div>
                                    <div className="text-text-default font-medium">
                                        {usage.height?.toFixed(2) ?? '-'} m
                                    </div>
                                </div>
                            </div>

                            <div className="mt-2 ml-2 px-1 grid grid-cols-[1.3fr_1fr] gap-2 text-sm text-text-light">
                                <div>
                                    {usage.coating && (
                                        <div className="mt-2 px-1 text-text-light">
                                            <div className="font-medium text-text-light uppercase text-xs underline">
                                                {t('labelCoating')}
                                            </div>
                                            <div className="text-text-default font-medium">{usage.coating}</div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="font-medium text-text-light uppercase text-xs underline">
                                        {t('labelTotalHeight')}
                                    </div>
                                    <div className="text-text-default font-medium">
                                        {usage.totalHeight != null ? `${usage.totalHeight.toFixed(2)}m` : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
